import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CHART_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const AuditLogs = ({ onNavigate, userInfo }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        page: 1,
        limit: 50
    });
    const [totalLogs, setTotalLogs] = useState(0);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('excel');
    const [exporting, setExporting] = useState(false);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { ...filters };
            const response = await axios.get(`${API_URL}/superadmin/audit-logs/${userInfo?.userId || ''}`, { params });
            if (response.data.success) {
                setLogs(response.data.logs);
                setTotalLogs(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, userInfo]);

    const fetchAnalytics = useCallback(async () => {
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            const response = await axios.get(`${API_URL}/superadmin/audit-analytics/${userInfo?.userId || ''}`, { params });
            if (response.data.success) {
                setAnalytics(response.data.analytics);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    }, [filters.startDate, filters.endDate, userInfo]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams({
                startDate: filters.startDate || '',
                endDate: filters.endDate || '',
                format: exportFormat,
                userId: userInfo?.userId || ''
            });

            const url = `${API_URL}/superadmin/audit-export?${params.toString()}`;
            window.open(url, '_blank');
            setShowExportModal(false);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Failed to export report');
        } finally {
            setExporting(false);
        }
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    return (
        <SuperAdminLayout
            onNavigate={onNavigate}
            userInfo={userInfo}
            activePage="hostel-superadmin-audit-logs"
            pageTitle="Audit Logs"
            icon="🛡️"
        >
            <style>{`
                .audit-page { max-width: 100%; }

                .audit-filters {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 20px;
                    align-items: center;
                    flex-wrap: wrap;
                    background: white;
                    padding: 16px 20px;
                    border-radius: 14px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.04);
                }

                .filter-group {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .filter-group label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #64748b;
                }

                .date-input {
                    padding: 8px 12px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    outline: none;
                    font-size: 13px;
                    transition: border-color 0.2s;
                }

                .date-input:focus { border-color: #6366f1; }

                .filter-actions {
                    display: flex;
                    gap: 8px;
                    margin-left: auto;
                }

                .btn {
                    padding: 8px 18px;
                    border-radius: 8px;
                    border: none;
                    font-weight: 600;
                    font-size: 13px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    color: white;
                    box-shadow: 0 2px 8px rgba(99,102,241,0.3);
                }
                .btn-primary:hover {
                    box-shadow: 0 4px 16px rgba(99,102,241,0.4);
                    transform: translateY(-1px);
                }

                .btn-outline {
                    background: white;
                    color: #6366f1;
                    border: 1.5px solid #e2e8f0;
                }
                .btn-outline:hover {
                    border-color: #6366f1;
                    background: #eef2ff;
                }

                .btn-toggle {
                    background: ${showAnalytics ? '#eef2ff' : 'white'};
                    color: ${showAnalytics ? '#6366f1' : '#64748b'};
                    border: 1.5px solid ${showAnalytics ? '#6366f1' : '#e2e8f0'};
                }
                .btn-toggle:hover {
                    border-color: #6366f1;
                    background: #eef2ff;
                }

                /* Analytics Section */
                .analytics-section {
                    margin-bottom: 20px;
                    animation: slideDown 0.3s ease;
                }

                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .stat-card {
                    background: white;
                    border-radius: 14px;
                    padding: 20px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.04);
                    position: relative;
                    overflow: hidden;
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                }

                .stat-card:nth-child(1)::before { background: linear-gradient(90deg, #6366f1, #8b5cf6); }
                .stat-card:nth-child(2)::before { background: linear-gradient(90deg, #06b6d4, #14b8a6); }
                .stat-card:nth-child(3)::before { background: linear-gradient(90deg, #f59e0b, #f97316); }
                .stat-card:nth-child(4)::before { background: linear-gradient(90deg, #10b981, #34d399); }

                .stat-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 6px;
                }

                .stat-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #1e293b;
                    line-height: 1;
                }

                .stat-icon {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    font-size: 24px;
                    opacity: 0.6;
                }

                .charts-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }

                .chart-card {
                    background: white;
                    border-radius: 14px;
                    padding: 20px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.04);
                }

                .chart-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .chart-title span { font-size: 16px; }

                /* Logs Table */
                .logs-card {
                    background: white;
                    border-radius: 14px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.04);
                    overflow: hidden;
                }

                .logs-card-header {
                    padding: 16px 20px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .logs-card-title {
                    font-size: 15px;
                    font-weight: 700;
                    color: #1e293b;
                }

                .logs-count {
                    font-size: 12px;
                    color: #94a3b8;
                    background: #f1f5f9;
                    padding: 4px 10px;
                    border-radius: 12px;
                    font-weight: 600;
                }

                .logs-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .logs-table th {
                    padding: 12px 16px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 12px;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .logs-table td {
                    padding: 12px 16px;
                    font-size: 13px;
                    color: #334155;
                    border-bottom: 1px solid #f1f5f9;
                }

                .logs-table tr:hover td { background: #f8fafc; }

                .role-badge {
                    display: inline-block;
                    padding: 3px 10px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .role-superadmin {
                    background: #eef2ff;
                    color: #6366f1;
                }

                .role-admin {
                    background: #ecfdf5;
                    color: #059669;
                }

                .role-student {
                    background: #fef3c7;
                    color: #d97706;
                }

                .role-default {
                    background: #f1f5f9;
                    color: #64748b;
                }

                .action-badge {
                    display: inline-block;
                    padding: 3px 8px;
                    border-radius: 5px;
                    font-size: 11px;
                    font-weight: 600;
                    font-family: monospace;
                    background: #f1f5f9;
                    color: #475569;
                }

                .details-cell {
                    max-width: 250px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #94a3b8;
                    font-size: 12px;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                    padding: 16px;
                    border-top: 1px solid #f1f5f9;
                }

                .page-btn {
                    padding: 6px 14px;
                    border: 1.5px solid #e2e8f0;
                    background: white;
                    cursor: pointer;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #64748b;
                    transition: all 0.2s;
                }

                .page-btn:hover:not(:disabled) {
                    border-color: #6366f1;
                    color: #6366f1;
                }

                .page-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .page-info {
                    display: flex;
                    align-items: center;
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 600;
                }

                /* Export Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15,23,42,0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .modal {
                    background: white;
                    padding: 28px;
                    border-radius: 16px;
                    width: 420px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.15);
                    animation: scaleIn 0.2s ease;
                }

                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .modal-title {
                    font-size: 18px;
                    font-weight: 800;
                    color: #1e293b;
                }

                .modal-close {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    border: none;
                    background: #f1f5f9;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    color: #64748b;
                    transition: all 0.2s;
                }

                .modal-close:hover {
                    background: #fee2e2;
                    color: #ef4444;
                }

                .format-option {
                    padding: 14px 16px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: all 0.2s;
                }

                .format-option:hover {
                    border-color: #c7d2fe;
                    background: #fafafe;
                }

                .format-option.selected {
                    border-color: #6366f1;
                    background: #eef2ff;
                }

                .format-icon { font-size: 28px; }

                .format-info h4 {
                    margin: 0;
                    font-size: 14px;
                    color: #1e293b;
                }

                .format-info p {
                    margin: 2px 0 0;
                    font-size: 12px;
                    color: #94a3b8;
                }

                .export-note {
                    font-size: 12px;
                    color: #94a3b8;
                    margin: 12px 0 16px;
                    padding: 8px 12px;
                    background: #f8fafc;
                    border-radius: 8px;
                    text-align: center;
                }

                .loading-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 60px;
                    color: #94a3b8;
                    font-size: 14px;
                }

                .no-data {
                    text-align: center;
                    padding: 40px;
                    color: #94a3b8;
                    font-size: 14px;
                }

                @media (max-width: 1024px) {
                    .stats-grid { grid-template-columns: repeat(2, 1fr); }
                    .charts-grid { grid-template-columns: 1fr; }
                }

                @media (max-width: 640px) {
                    .stats-grid { grid-template-columns: 1fr; }
                    .audit-filters { flex-direction: column; }
                    .filter-actions { margin-left: 0; width: 100%; justify-content: flex-end; }
                }
            `}</style>

            <div className="audit-page">
                {/* Filters Bar */}
                <div className="audit-filters">
                    <div className="filter-group">
                        <label>Start Date:</label>
                        <input type="date" name="startDate" className="date-input" value={filters.startDate} onChange={handleFilterChange} />
                    </div>
                    <div className="filter-group">
                        <label>End Date:</label>
                        <input type="date" name="endDate" className="date-input" value={filters.endDate} onChange={handleFilterChange} />
                    </div>
                    <div className="filter-actions">
                        <button className="btn btn-toggle" onClick={() => setShowAnalytics(!showAnalytics)}>
                            📊 {showAnalytics ? 'Hide' : 'Show'} Analytics
                        </button>
                        <button className="btn btn-primary" onClick={() => setShowExportModal(true)}>
                            📥 Export Report
                        </button>
                    </div>
                </div>

                {/* Analytics Dashboard */}
                {showAnalytics && analytics && (
                    <div className="analytics-section">
                        {/* Summary Stats Cards */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-label">Total Events</div>
                                <div className="stat-value">{analytics.totalLogs?.toLocaleString() || 0}</div>
                                <div className="stat-icon">📋</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">Unique Users</div>
                                <div className="stat-value">{analytics.uniqueUsers || 0}</div>
                                <div className="stat-icon">👥</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">Most Active Role</div>
                                <div className="stat-value" style={{ fontSize: '18px', wordBreak: 'break-word' }}>{analytics.mostActiveRole || 'N/A'}</div>
                                <div className="stat-icon">🏆</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">Peak Activity Hour</div>
                                <div className="stat-value">{analytics.peakHour || '—'}</div>
                                <div className="stat-icon">⏰</div>
                            </div>
                        </div>

                        {/* Charts */}
                        <div className="charts-grid">
                            {/* Actions by Type */}
                            <div className="chart-card">
                                <div className="chart-title"><span>📊</span> Actions by Type</div>
                                {analytics.actionDistribution?.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={analytics.actionDistribution.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis type="number" fontSize={11} tick={{ fill: '#94a3b8' }} />
                                            <YAxis type="category" dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} width={110} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                                                formatter={(value) => [value, 'Count']}
                                            />
                                            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                                {analytics.actionDistribution.slice(0, 8).map((_, idx) => (
                                                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : <div className="no-data">No data available</div>}
                            </div>

                            {/* Activity by Role */}
                            <div className="chart-card">
                                <div className="chart-title"><span>🎭</span> Activity by Role</div>
                                {analytics.roleDistribution?.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={analytics.roleDistribution}
                                                cx="50%" cy="50%"
                                                innerRadius={55} outerRadius={85}
                                                paddingAngle={4}
                                                dataKey="count"
                                                nameKey="name"
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                labelLine={false}
                                                fontSize={10}
                                            >
                                                {analytics.roleDistribution.map((_, idx) => (
                                                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                                                formatter={(value, name) => [value, name]}
                                            />
                                            <Legend fontSize={11} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : <div className="no-data">No data available</div>}
                            </div>

                            {/* Activity Timeline */}
                            <div className="chart-card">
                                <div className="chart-title"><span>📈</span> Hourly Activity Pattern</div>
                                {analytics.hourlyActivity?.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <AreaChart data={analytics.hourlyActivity} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                                            <defs>
                                                <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="hour" fontSize={10} tick={{ fill: '#94a3b8' }} interval={2} />
                                            <YAxis fontSize={11} tick={{ fill: '#94a3b8' }} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                                                formatter={(value) => [value, 'Events']}
                                            />
                                            <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2}
                                                fill="url(#colorActivity)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : <div className="no-data">No data available</div>}
                            </div>

                            {/* Top Users */}
                            <div className="chart-card">
                                <div className="chart-title"><span>👤</span> Top Active Users</div>
                                {analytics.topUsers?.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={analytics.topUsers.slice(0, 6)} margin={{ left: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="name" fontSize={10} tick={{ fill: '#94a3b8' }} interval={0}
                                                tickFormatter={(val) => val.length > 12 ? val.substring(0, 12) + '…' : val} />
                                            <YAxis fontSize={11} tick={{ fill: '#94a3b8' }} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                                                formatter={(value) => [value, 'Actions']}
                                            />
                                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                                {analytics.topUsers.slice(0, 6).map((_, idx) => (
                                                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : <div className="no-data">No data available</div>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Logs Table */}
                <div className="logs-card">
                    <div className="logs-card-header">
                        <span className="logs-card-title">📋 Detailed Audit Logs</span>
                        <span className="logs-count">{totalLogs} total entries</span>
                    </div>

                    {loading ? (
                        <div className="loading-container">Loading logs...</div>
                    ) : (
                        <>
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th>Date & Time</th>
                                        <th>User</th>
                                        <th>Role</th>
                                        <th>Action</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length > 0 ? logs.map(log => {
                                        const roleClass = (log.role || '').includes('superadmin') ? 'role-superadmin'
                                            : (log.role || '').includes('admin') ? 'role-admin'
                                                : (log.role || '').includes('student') ? 'role-student'
                                                    : 'role-default';

                                        return (
                                            <tr key={log.id}>
                                                <td>{new Date(log.created_at).toLocaleString()}</td>
                                                <td>{log.user_email || 'System'}</td>
                                                <td><span className={`role-badge ${roleClass}`}>{log.role}</span></td>
                                                <td><span className="action-badge">{log.action}</span></td>
                                                <td className="details-cell" title={typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}>
                                                    {typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr><td colSpan="5" className="no-data">No logs found for the selected period.</td></tr>
                                    )}
                                </tbody>
                            </table>

                            <div className="pagination">
                                <button className="page-btn" disabled={filters.page === 1} onClick={() => handlePageChange(filters.page - 1)}>← Previous</button>
                                <span className="page-info">Page {filters.page}</span>
                                <button className="page-btn" disabled={logs.length < filters.limit} onClick={() => handlePageChange(filters.page + 1)}>Next →</button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Export Modal */}
            {showExportModal && (
                <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title">📥 Export Audit Report</span>
                            <button className="modal-close" onClick={() => setShowExportModal(false)}>✕</button>
                        </div>

                        <div style={{ marginBottom: '8px' }}>
                            <div
                                className={`format-option ${exportFormat === 'excel' ? 'selected' : ''}`}
                                onClick={() => setExportFormat('excel')}
                            >
                                <span className="format-icon">📊</span>
                                <div className="format-info">
                                    <h4>Excel Spreadsheet (.xlsx)</h4>
                                    <p>Includes analytics summary sheet + detailed logs</p>
                                </div>
                            </div>
                            <div
                                className={`format-option ${exportFormat === 'pdf' ? 'selected' : ''}`}
                                onClick={() => setExportFormat('pdf')}
                            >
                                <span className="format-icon">📄</span>
                                <div className="format-info">
                                    <h4>PDF Document (.pdf)</h4>
                                    <p>Professional report with analytics & data tables</p>
                                </div>
                            </div>
                        </div>

                        <div className="export-note">
                            ✨ Report includes analytics summary, action distribution, top users & full audit log data
                        </div>

                        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} onClick={handleExport} disabled={exporting}>
                            {exporting ? '⏳ Generating...' : '📥 Download Report'}
                        </button>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default AuditLogs;
